import React, { useState, useEffect } from 'react';

interface AnnotationTask {
  taskId: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'approved';
  items: string[];
  createdAt: string;
  updatedAt: string;
}

interface AnnotationStats {
  totalTasks: number;
  totalAnnotations: number;
  lastUpdated: string;
}

export const DeveloperPortalTab: React.FC<{ 'data-devinid'?: string }> = ({ 'data-devinid': devinId }) => {
  const [tasks, setTasks] = useState<AnnotationTask[]>([]);
  const [stats, setStats] = useState<AnnotationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskItems, setNewTaskItems] = useState('');

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/annotation/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: 'list' })
      });
      const data = await response.json();
      if (data.success) {
        setTasks(data.data.tasks || []);
      } else {
        setError(data.message || 'Failed to fetch tasks');
      }
    } catch {
      setError('Network error while fetching tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/annotation/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: 'stats' })
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const createTask = async () => {
    if (!newTaskName.trim() || !newTaskItems.trim()) {
      setError('Task name and items are required');
      return;
    }

    try {
      setLoading(true);
      const items = newTaskItems.split('\n').map(item => item.trim()).filter(Boolean);
      const response = await fetch('/api/annotation/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command: 'create',
          name: newTaskName,
          items
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewTaskName('');
        setNewTaskItems('');
        fetchTasks();
        fetchStats();
      } else {
        setError(data.message || 'Failed to create task');
      }
    } catch {
      setError('Network error while creating task');
    } finally {
      setLoading(false);
    }
  };

  const exportTask = async (taskId: string, format: 'json' | 'csv') => {
    try {
      const response = await fetch('/api/annotation/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command: 'export',
          taskId,
          format
        })
      });
      const data = await response.json();
      if (data.success) {
        const blob = new Blob([data.data], { 
          type: format === 'json' ? 'application/json' : 'text/csv' 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `annotations_${taskId}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        setError(data.message || 'Failed to export task');
      }
    } catch {
      setError('Network error while exporting task');
    }
  };

  const deleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch('/api/annotation/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command: 'delete',
          taskId
        })
      });
      const data = await response.json();
      if (data.success) {
        fetchTasks();
        fetchStats();
      } else {
        setError(data.message || 'Failed to delete task');
      }
    } catch {
      setError('Network error while deleting task');
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, []);

  return (
    <div data-devinid={devinId} className="annotation-developer-portal p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Annotation Developer Portal</h1>
        <p className="text-gray-600">Manage annotation tasks and export data for ML training</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900">Total Tasks</h3>
            <p className="text-3xl font-bold text-blue-600">{stats.totalTasks}</p>
          </div>
          <div className="bg-green-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-green-900">Total Annotations</h3>
            <p className="text-3xl font-bold text-green-600">{stats.totalAnnotations}</p>
          </div>
          <div className="bg-purple-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-900">Last Updated</h3>
            <p className="text-sm text-purple-600">{new Date(stats.lastUpdated).toLocaleString()}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Create New Task</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Task Name</label>
            <input
              type="text"
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter task name"
              data-devinid="new-task-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Items (one per line)</label>
            <textarea
              value={newTaskItems}
              onChange={(e) => setNewTaskItems(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="image1.jpg&#10;image2.jpg&#10;image3.jpg"
              data-devinid="new-task-items-input"
            />
          </div>
        </div>
        <button
          onClick={createTask}
          disabled={loading}
          className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          data-devinid="create-task-button"
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="loading-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
              <span>Creating...</span>
            </div>
          ) : 'Create Task'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Annotation Tasks</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((task) => (
                <tr key={task.taskId} data-devinid={`task-row-${task.taskId}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{task.name}</div>
                    <div className="text-sm text-gray-500">{task.taskId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      task.status === 'completed' ? 'bg-green-100 text-green-800' :
                      task.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                      task.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {task.items.length} items
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(task.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => exportTask(task.taskId, 'json')}
                      className="text-blue-600 hover:text-blue-900"
                      data-devinid={`export-json-${task.taskId}`}
                    >
                      Export JSON
                    </button>
                    <button
                      onClick={() => exportTask(task.taskId, 'csv')}
                      className="text-green-600 hover:text-green-900"
                      data-devinid={`export-csv-${task.taskId}`}
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={() => deleteTask(task.taskId)}
                      className="text-red-600 hover:text-red-900"
                      data-devinid={`delete-task-${task.taskId}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tasks.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              No annotation tasks found. Create your first task above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeveloperPortalTab;
